import { Phone, AlertTriangle, RotateCcw, Building2, Gauge } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip } from "recharts";
import { useEffect, useState } from "react";
import { TYPE_BY_VALUE } from "../../lib/propertyTypes";
import { fmtUSDate, fmtUSDateTime } from "../../lib/date";

/**
 * Toll-free call-attempts card. Renders: 7-day line chart, by-role breakdown,
 * 3-step conversion funnel (Called → Signed up → Activated), top source pages,
 * recent attempts list. Used on the Admin Revenue Dashboard.
 */
export function CallAttemptsCard({ calls }) {
  if (!calls) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden mb-6" data-testid="calls-card">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-slate-500" />
          <div>
            <div className="overline text-slate-500">Inbound</div>
            <div className="text-sm font-semibold text-slate-900">Toll-free call attempts</div>
          </div>
        </div>
        <div className="flex items-center gap-6 text-xs font-mono">
          <div><span className="text-slate-500">Total</span> <span className="text-slate-900 font-bold ml-1" data-testid="calls-total">{calls.total}</span></div>
          <div><span className="text-slate-500">Today</span> <span className="text-emerald-600 font-bold ml-1" data-testid="calls-today">{calls.today}</span></div>
        </div>
      </div>
      <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 h-[240px]">
          <div className="overline text-slate-500 mb-2">Last 7 days</div>
          <ResponsiveContainer>
            <LineChart data={calls.trend}>
              <CartesianGrid strokeDasharray="2 4" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="day" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #e2e8f0", fontSize: 12 }} />
              <Line type="monotone" dataKey="count" name="Calls" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div>
          <div className="overline text-slate-500 mb-3">By user role</div>
          <ul className="space-y-2 text-sm" data-testid="calls-by-role">
            {calls.by_role.length === 0 && <li className="text-slate-400">No calls yet.</li>}
            {calls.by_role.map((r) => (
              <li key={r.role} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                <span className="overline text-slate-600">{r.role}</span>
                <span className="font-mono font-bold text-slate-900">{r.count}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      {calls.funnel && (
        <div className="border-t border-slate-200 p-5" data-testid="calls-funnel">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div>
              <div className="overline text-slate-500">Attribution</div>
              <div className="text-sm font-semibold text-slate-900">Conversion funnel · Called → Signed up → Activated</div>
            </div>
            <div className="text-xs font-mono text-slate-500">
              Overall <span className={`font-bold ml-1 ${calls.funnel.overall_rate >= 10 ? "text-green-600" : calls.funnel.overall_rate >= 5 ? "text-amber-600" : "text-slate-700"}`} data-testid="funnel-overall">{calls.funnel.overall_rate}%</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <FunnelStep step="01" label="Called" value={calls.funnel.called}
              note={`${calls.funnel.called_known} known · ${calls.funnel.called_anonymous} anon`}
              fill={100} color="bg-slate-900" testid="funnel-step-called" />
            <FunnelStep step="02" label="Signed up" value={calls.funnel.signed_up}
              note={`${calls.funnel.call_to_signup_rate}% of callers`}
              fill={calls.funnel.called ? (calls.funnel.signed_up / calls.funnel.called) * 100 : 0}
              color="bg-blue-600" testid="funnel-step-signup" />
            <FunnelStep step="03" label="Activated" value={calls.funnel.activated}
              note={`${calls.funnel.signup_to_active_rate}% of signups`}
              fill={calls.funnel.called ? (calls.funnel.activated / calls.funnel.called) * 100 : 0}
              color="bg-emerald-600" testid="funnel-step-activated" />
          </div>
          {calls.funnel.activated_emails.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5" data-testid="funnel-activated-list">
              <span className="overline text-slate-500 mr-2 flex items-center">Converted customers</span>
              {calls.funnel.activated_emails.slice(0, 20).map((e) => (
                <span key={e} className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5 text-[11px] font-mono text-emerald-900">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {e}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border-t border-slate-200">
        <div className="p-5 border-b lg:border-b-0 lg:border-r border-slate-200">
          <div className="overline text-slate-500 mb-3">Top source pages</div>
          {calls.by_page.length === 0 ? (
            <div className="text-sm text-slate-400">No calls yet.</div>
          ) : (
            <ul className="space-y-1.5 text-sm" data-testid="calls-by-page">
              {calls.by_page.slice(0, 8).map((p) => (
                <li key={p.page} className="flex items-center justify-between py-1 border-b border-slate-50 last:border-0">
                  <span className="font-mono text-xs text-slate-700 truncate max-w-[75%]">{p.page || "/"}</span>
                  <span className="font-mono font-bold text-slate-900">{p.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="p-5">
          <div className="overline text-slate-500 mb-3">Recent attempts</div>
          {calls.recent.length === 0 ? (
            <div className="text-sm text-slate-400">No calls yet.</div>
          ) : (
            <ul className="space-y-2 text-sm max-h-[240px] overflow-y-auto" data-testid="calls-recent">
              {calls.recent.slice(0, 12).map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-3 py-1 border-b border-slate-50 last:border-0">
                  <div className="min-w-0">
                    <div className="text-xs font-mono truncate text-slate-900">{c.user_email || "Anonymous"}</div>
                    <div className="text-[11px] text-slate-500 truncate">{c.page || "/"} · <span className="uppercase tracking-widest">{c.user_role}</span></div>
                  </div>
                  <div className="text-[11px] font-mono text-slate-500 whitespace-nowrap">
                    {fmtUSDateTime(c.created_at)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function FunnelStep({ step, label, value, note, fill, color, testid }) {
  return (
    <div data-testid={testid} className="border border-slate-200 rounded-sm p-4 bg-slate-50/30">
      <div className="flex items-baseline justify-between mb-1">
        <span className="font-mono text-[10px] text-slate-400 tracking-widest">STEP {step}</span>
        <span className="overline text-slate-500">{label}</span>
      </div>
      <div className="text-3xl font-bold tracking-tight font-mono text-slate-900 mt-1">{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{note}</div>
      <div className="mt-3 h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all duration-700`} style={{ width: `${Math.max(4, Math.min(100, fill))}%` }} />
      </div>
    </div>
  );
}


/**
 * Cross-owner property-claim collision moderation queue. Admin can mark a
 * collision resolved (with notes) or reopen a resolved one. Shows all claims
 * per place_id with each owner's details. Empty when duplicates=null or 0.
 */
export function DuplicateClaimsCard({ duplicates, showResolved, onToggleResolved, onResolve, onUnresolve }) {
  if (!duplicates || (duplicates.count === 0 && duplicates.resolved_count === 0)) return null;
  return (
    <div className="bg-white border border-amber-300 rounded-sm overflow-hidden mb-6" data-testid="duplicate-claims-card">
      <div className="px-5 py-4 border-b border-amber-200 bg-amber-50 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <div>
            <div className="overline text-amber-700">Integrity</div>
            <div className="text-sm font-semibold text-amber-900">
              Cross-owner property claim collisions
              <span className="ml-2 text-xs font-mono font-normal text-amber-700">
                {duplicates.count} active
                {duplicates.resolved_count > 0 && ` · ${duplicates.resolved_count} resolved`}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {duplicates.resolved_count > 0 && (
            <label className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-amber-800 cursor-pointer" data-testid="toggle-show-resolved">
              <input
                type="checkbox"
                checked={showResolved}
                onChange={(e) => onToggleResolved(e.target.checked)}
                className="accent-amber-600"
              />
              Show resolved
            </label>
          )}
          <div className="text-xs text-amber-800 max-w-xs">
            Same Google Place ID claimed by ≥2 investors.
          </div>
        </div>
      </div>
      {duplicates.count === 0 ? (
        <div className="p-10 text-center text-sm text-slate-500">
          <span className="text-emerald-600 font-semibold">✓ All clear</span> — no unresolved collisions right now.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {duplicates.items.slice(0, 20).map((collision) => (
            <CollisionRow key={collision.place_id} collision={collision} onResolve={onResolve} onUnresolve={onUnresolve} />
          ))}
        </ul>
      )}
      {duplicates.items.length > 20 && (
        <div className="px-5 py-3 text-center text-xs text-slate-500 font-mono bg-slate-50 border-t border-slate-200">
          Showing 20 of {duplicates.count} collisions.
        </div>
      )}
    </div>
  );
}

function CollisionRow({ collision, onResolve, onUnresolve }) {
  return (
    <li
      className={`p-5 ${collision.resolved ? "bg-slate-50/60 opacity-70" : ""}`}
      data-testid={`collision-${collision.place_id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-start gap-2 min-w-0">
          <Building2 className="w-4 h-4 text-slate-500 mt-1 shrink-0" />
          <div className="min-w-0">
            <div className="font-mono text-xs text-slate-500 tracking-widest uppercase">Place ID</div>
            <div className="font-mono text-sm text-slate-900 break-all">{collision.place_id}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-mono uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-200">
            {collision.owner_count} owners · {collision.total_claims} claims
          </span>
          {collision.resolved ? (
            <button
              onClick={() => onUnresolve(collision.place_id)}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-sm text-[11px] font-mono uppercase tracking-widest bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200"
              data-testid={`unresolve-${collision.place_id}`}
            >
              <RotateCcw className="w-3 h-3" /> Reopen
            </button>
          ) : (
            <button
              onClick={() => onResolve(collision.place_id)}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-sm text-[11px] font-mono uppercase tracking-widest bg-emerald-600 text-white hover:bg-emerald-700"
              data-testid={`resolve-${collision.place_id}`}
            >
              ✓ Mark resolved
            </button>
          )}
        </div>
      </div>
      {collision.resolved && collision.resolution && (
        <div className="mb-3 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2 font-mono">
          <b>Resolved</b> by {collision.resolution.resolved_by_email || "admin"} on{" "}
          {fmtUSDate(collision.resolution.resolved_at)}
          {collision.resolution.notes && <> — "{collision.resolution.notes}"</>}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {collision.claims.map((claim) => (
          <div key={claim.id} className="bg-slate-50 border border-slate-200 rounded-sm p-3" data-testid={`claim-${claim.id}`}>
            <div className="font-bold text-slate-900 text-sm">{claim.name}</div>
            <div className="text-xs text-slate-500 font-mono mt-0.5 truncate">
              {claim.formatted_address || claim.address}
            </div>
            {claim.owner && (
              <div className="mt-2 pt-2 border-t border-slate-200 text-xs">
                <div className="text-slate-700 font-medium truncate">{claim.owner.full_name}</div>
                <div className="text-slate-500 font-mono truncate">{claim.owner.email}</div>
                {claim.owner.company && <div className="text-slate-400 truncate">{claim.owner.company}</div>}
                <div className="mt-1 flex items-center gap-2">
                  <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${claim.owner.subscription_plan ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                    {claim.owner.subscription_plan || "trial"}
                  </span>
                  <span className="text-[10px] text-slate-400 font-mono">
                    joined {fmtUSDate(claim.owner.created_at)}
                  </span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </li>
  );
}


/**
 * Cap-rate benchmark editor. Investor sees default ranges per property type;
 * admin can override (and reset) with full audit trail.
 */
export function BenchmarksCard({ benchmarks, onSave, onReset }) {
  if (!benchmarks) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden mb-6" data-testid="benchmarks-card">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Gauge className="w-4 h-4 text-cyan-600" />
          <div>
            <div className="overline text-slate-500">Configuration</div>
            <div className="text-sm font-semibold text-slate-900">Cap-rate benchmarks by property type</div>
          </div>
        </div>
        <div className="text-xs text-slate-500">Edits apply to dashboards, properties list, snapshot PDF, and watchlist hints.</div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-left">
            <th className="px-5 py-3 overline text-slate-600">Type</th>
            <th className="px-5 py-3 overline text-slate-600 text-right">Default</th>
            <th className="px-5 py-3 overline text-slate-600 text-right">Min %</th>
            <th className="px-5 py-3 overline text-slate-600 text-right">Max %</th>
            <th className="px-5 py-3 overline text-slate-600 text-center">Status</th>
            <th className="px-5 py-3 overline text-slate-600">Last Updated</th>
            <th className="px-5 py-3 overline text-slate-600 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {benchmarks.rows.map((r) => (
            <BenchmarkRow key={r.type} row={r} onSave={onSave} onReset={onReset} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BenchmarkRow({ row, onSave, onReset }) {
  const [min, setMin] = useState(row.effective_min ?? "");
  const [max, setMax] = useState(row.effective_max ?? "");
  useEffect(() => {
    setMin(row.effective_min ?? "");
    setMax(row.effective_max ?? "");
  }, [row.effective_min, row.effective_max]);

  const dirty = String(min) !== String(row.effective_min ?? "") || String(max) !== String(row.effective_max ?? "");
  const meta = TYPE_BY_VALUE[row.type];
  const hasDefault = row.default_min != null && row.default_max != null;

  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`benchmark-row-${row.type}`}>
      <td className="px-5 py-3">
        <div className="inline-flex items-center gap-2">
          <span className="text-base" aria-hidden="true">{meta?.emoji || "📦"}</span>
          <span className="font-semibold text-slate-900">{meta?.label || row.type}</span>
        </div>
      </td>
      <td className="px-5 py-3 text-right font-mono text-xs text-slate-500">
        {hasDefault ? `${row.default_min}–${row.default_max}%` : "—"}
      </td>
      <td className="px-5 py-3 text-right">
        <input
          type="number" step="0.1" min="0" max="50"
          className="input-clean w-20 text-right font-mono"
          data-testid={`benchmark-min-${row.type}`}
          value={min} onChange={(e) => setMin(e.target.value)}
        />
      </td>
      <td className="px-5 py-3 text-right">
        <input
          type="number" step="0.1" min="0" max="50"
          className="input-clean w-20 text-right font-mono"
          data-testid={`benchmark-max-${row.type}`}
          value={max} onChange={(e) => setMax(e.target.value)}
        />
      </td>
      <td className="px-5 py-3 text-center">
        {row.is_override ? (
          <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest" data-testid={`benchmark-status-${row.type}`}>Override</span>
        ) : (
          <span className="inline-flex items-center gap-1 bg-slate-50 text-slate-500 border border-slate-200 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest" data-testid={`benchmark-status-${row.type}`}>Default</span>
        )}
      </td>
      <td className="px-5 py-3 text-xs" data-testid={`benchmark-audit-${row.type}`}>
        {row.updated_at ? (
          <div>
            <div className="font-mono text-slate-700">{fmtUSDateTime(row.updated_at)}</div>
            <div className="text-[10px] text-slate-500 truncate max-w-[200px]">{row.updated_by || "—"}</div>
          </div>
        ) : <span className="text-slate-400">—</span>}
      </td>
      <td className="px-5 py-3 text-right">
        <div className="inline-flex items-center gap-2">
          <button
            onClick={() => onSave(row.type, min, max)}
            disabled={!dirty || min === "" || max === "" || Number(min) >= Number(max)}
            data-testid={`benchmark-save-${row.type}`}
            className="btn-primary-dark text-xs px-3 py-1.5 disabled:opacity-40"
          >Save</button>
          {row.is_override && (
            <button
              onClick={() => onReset(row.type)}
              data-testid={`benchmark-reset-${row.type}`}
              className="text-slate-500 hover:text-slate-900 p-1.5 transition-colors"
              title="Clear override, revert to default"
            ><RotateCcw className="w-4 h-4" /></button>
          )}
        </div>
      </td>
    </tr>
  );
}
